import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Award, ArrowRight, Mail, Lock, User as UserIcon, ShieldCheck, AlertTriangle } from "lucide-react";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/invites/${token}`);
        if (!alive) return;
        setInvite(res.data);
        if (res.data.invited_email) setEmail(res.data.invited_email);
      } catch (err) {
        setError(err?.response?.data?.detail || "Convite inválido.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const claimAndCheckout = async () => {
    setClaiming(true);
    setError("");
    try {
      const res = await api.post("/api/invites/claim", { token });
      // Beta: skip Stripe. Ensure subscription is active and land on the map.
      if (res.data.status !== "enrolled_waived") {
        try {
          await api.post("/api/onboarding/beta-activate");
        } catch (activateErr) {
          // Non-fatal: invite claim already grants access; log for ops.
          console.warn("beta-activate failed on invite accept (ignored):", activateErr);
        }
      }
      await refreshUser();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Erro ao aceitar convite.");
    } finally {
      setClaiming(false);
    }
  };

  // Auto-claim when a logged in user hits the invite page.
  useEffect(() => {
    if (!user || !invite) return;
    if (invite.status === "accepted") {
      navigate("/", { replace: true });
    }
  }, [user, invite, navigate]);

  const submitSignup = async (e) => {
    e.preventDefault();
    if (mode === "signup" && password !== passwordConfirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setProcessing(true);
    setError("");
    try {
      if (mode === "signup") {
        const { data, error: signErr } = await supabase.auth.signUp({
          email: email.toLowerCase(),
          password,
          options: { data: { name, invite_token: token, signup_intent: "student" } },
        });
        if (signErr) throw signErr;
        if (!data.session) {
          // Try immediate password login (works for auto-confirm projects).
          const { data: signIn, error: siErr } = await supabase.auth.signInWithPassword({
            email: email.toLowerCase(),
            password,
          });
          if (siErr) throw siErr;
          if (!signIn.session) {
            setError("Cadastro realizado. Confirme seu e-mail e volte a este link.");
            return;
          }
        }
      } else {
        const { error: signErr } = await supabase.auth.signInWithPassword({
          email: email.toLowerCase(),
          password,
        });
        if (signErr) throw signErr;
      }
      await refreshUser();
      await claimAndCheckout();
    } catch (err) {
      setError(err?.message || "Erro no cadastro.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
          <span className="text-xs text-gray-300 font-semibold">Carregando convite...</span>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-6">
        <div className="max-w-sm text-center bg-[#14161C] border border-red-500/30 rounded-2xl p-6">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-300" data-testid="invite-error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="invite-page-container"
      className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md space-y-4">
        <div className="text-center mb-2">
          <div className="w-14 h-14 rounded-xl bg-[#FF6B00]/20 border border-[#FF6B00]/50 flex items-center justify-center text-[#FF6B00] mx-auto mb-3">
            <Award className="w-7 h-7" />
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-white">
            Você foi convidado(a)
          </h1>
          <p className="text-xs text-gray-300 mt-1">
            Convite para entrar em{" "}
            <span data-testid="invite-mentorship-name" className="font-bold text-[#FF6B00]">
              {invite?.mentorship_name}
            </span>
          </p>
        </div>

        <div className="bg-[#14161C] border border-[#282D3D] rounded-2xl p-5 shadow-2xl">
          {user ? (
            <div className="text-center space-y-3">
              <p className="text-xs text-gray-300">
                Você está logado como <strong className="text-white">{user.email}</strong>.
              </p>
              <button
                data-testid="invite-continue-btn"
                onClick={claimAndCheckout}
                disabled={claiming}
                className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>{claiming ? "Processando..." : "Aceitar convite e entrar"}</span>
              </button>
            </div>
          ) : (
            <>
              {/*
                REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
                REDIRECT URLS, THIS BREAKS THE AUTH.
                The redirect must be dynamic via window.location.origin
                so dev/preview/prod all return the user to their own domain.
              */}
              <button
                type="button"
                data-testid="invite-google-signin-btn"
                onClick={() => {
                  const redirectUrl =
                    window.location.origin +
                    `/auth/callback?invite=${encodeURIComponent(token || "")}`;
                  window.location.href =
                    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full mb-3 py-2.5 rounded-lg bg-white hover:bg-gray-100 text-black font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <svg aria-hidden="true" className="w-4 h-4" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                  <path fill="none" d="M0 0h48v48H0z" />
                </svg>
                <span>Entrar com Google</span>
              </button>
              <div className="flex items-center gap-2 mb-3">
                <span className="flex-1 h-px bg-[#282D3D]" />
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                  ou com e-mail
                </span>
                <span className="flex-1 h-px bg-[#282D3D]" />
              </div>

              <div className="flex border-b border-[#282D3D] mb-4">
                <button
                  type="button"
                  data-testid="invite-tab-signup"
                  onClick={() => setMode("signup")}
                  className={`flex-1 pb-2 text-xs font-bold transition-colors ${
                    mode === "signup" ? "text-[#FF6B00] border-b-2 border-[#FF6B00]" : "text-gray-400"
                  }`}
                >
                  Criar conta
                </button>
                <button
                  type="button"
                  data-testid="invite-tab-login"
                  onClick={() => setMode("login")}
                  className={`flex-1 pb-2 text-xs font-bold transition-colors ${
                    mode === "login" ? "text-[#FF6B00] border-b-2 border-[#FF6B00]" : "text-gray-400"
                  }`}
                >
                  Já tenho conta
                </button>
              </div>

              <form onSubmit={submitSignup} className="space-y-3">
                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1">Nome</label>
                    <div className="relative">
                      <UserIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                      <input
                        type="text"
                        required
                        data-testid="invite-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">E-mail</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="email"
                      required
                      data-testid="invite-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Senha</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      data-testid="invite-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                    />
                  </div>
                </div>

                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1">Confirmar Senha</label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                      <input
                        type="password"
                        required
                        minLength={6}
                        data-testid="invite-password-confirm-input"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        className={`w-full bg-[#1A1D26] border rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none ${
                          passwordConfirm && passwordConfirm !== password
                            ? "border-red-500 focus:border-red-500"
                            : "border-[#282D3D] focus:border-[#FF6B00]"
                        }`}
                      />
                    </div>
                    {passwordConfirm && passwordConfirm !== password && (
                      <p className="text-[10px] text-red-400 mt-1">As senhas não coincidem.</p>
                    )}
                  </div>
                )}

                {error && (
                  <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-300">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={processing}
                  data-testid="invite-submit-btn"
                  className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <span>{processing ? "Processando..." : mode === "signup" ? "Criar conta e assinar" : "Entrar e assinar"}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-[10px] text-center text-gray-500">
          Ao aceitar, você concorda em iniciar sua assinatura de aluno para
          participar da mentoria.
        </p>
      </div>
    </div>
  );
}
